import { useState, useEffect, useRef } from "react";
import { useAuth } from '../../../context/AuthProvider';
import { baseCurrencyOf, normalizeMode, planValueError } from '../../../utils/market';
import { authService } from "../../../services/api";
import { useNotification } from "../../../components/Notifications/NotificationContext";
import { MediaManager } from "../../../components/MediaManager/MediaManager";
import PlanManager from "../../../components/PlanManager/PlanManager";
import { TextForm } from "../../../components/TextForm/TextForm";
import { validateFields } from "../../../utils/contentBlacklistValidator";
import { deliveriesFromApi } from "../../../utils/apiPlansToForm";
import PersonalizedOrderBump from "../OrderBump/PersonalizedOrderBump";
import {
    ArrowLeft, ArrowRight, Save, Check,
    Sparkles, MessageSquare, Package,
    Users, CircleCheckBig, CircleAlert, Bot,
    UserX, ArrowDown, ArrowUp, Mail, Repeat, Gift,
} from "lucide-react";
import { useBotPhotos } from "../../../hooks/useBotPhotos";
import { useGatewayList } from "../../../hooks/useOnlyUpsellBlocked";
import { isOnlyUpsellBlocked } from "../../../utils/gateways";
import MarketIcon from '../../../components/Flags/MarketIcon';

// `value` é gravado no disparo e precisa casar com o backend (disparo_service.AUDIENCE_*).
const AUDIENCES = [
    { value: "Todos Usuários", label: "Todos Usuários", icon: Users, desc: "Todo mundo que deu /start" },
    { value: "Usuários Pagantes", label: "Usuários Pagantes", icon: CircleCheckBig, desc: "Já compraram" },
    { value: "Usuários Pendentes", label: "Usuários Pendentes", icon: CircleAlert, desc: "Geraram pagamento e não pagaram" },
    { value: "Não Compradores", label: "Não Compradores", icon: UserX, desc: "Nunca compraram (só /start ou gerou e não pagou)" },
    { value: "Downsellers", label: "Downsellers", icon: ArrowDown, desc: "Compraram um downsell" },
    { value: "Upsellers", label: "Upsellers", icon: ArrowUp, desc: "Compraram um upsell" },
    { value: "Compradores de Mailing", label: "Compradores de Mailing", icon: Mail, desc: "Compraram por um disparo" },
    { value: "Recorrentes", label: "Recorrentes", icon: Repeat, desc: "2 ou mais compras" },
    { value: "Compradores de Order Bump", label: "Compradores de Order Bump", icon: Gift, desc: "Levaram um order bump" },
];

const STEPS = [
    { id: 1, label: "Configuração", icon: Sparkles },
    { id: 2, label: "Público", icon: Users },
    { id: 3, label: "Mensagem", icon: MessageSquare },
    { id: 4, label: "Planos", icon: Package },
];

/* ── Audience grid ── */
const fmtCount = (n) => Number(n).toLocaleString("pt-BR");

function AudienceGrid({ value, onChange, counts, countsLoading }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {AUDIENCES.map(({ value: v, label, icon: Icon, desc }) => {
                const active = value === v;
                return (
                    <button
                        key={v}
                        type="button"
                        onClick={() => onChange(v)}
                        className={[
                            "p-4 rounded-xl border-2 text-left transition-all cursor-pointer",
                            active
                                ? "border-[var(--purple)] bg-[var(--purple)]/5"
                                : "border-white/[0.06] bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]",
                        ].join(" ")}
                    >
                        <div className="flex items-center gap-3">
                            <Icon size={17} className={active ? "text-[var(--purple)]" : "text-white/40"} />
                            <span className={`text-sm font-medium ${active ? "text-[var(--purple)]" : "text-white/80"}`}>
                                {label}
                            </span>
                        </div>
                        {desc && <p className="mt-1 ml-[29px] text-[11px] leading-snug text-white/40">{desc}</p>}
                        {(countsLoading || (counts && counts[v] != null)) && (
                            <p className={`mt-1.5 ml-[29px] text-xs font-semibold tabular-nums ${active ? "text-[var(--purple)]" : "text-white/70"}`}>
                                {countsLoading && !counts
                                    ? <span className="inline-block w-16 h-3 rounded bg-white/[0.08] animate-pulse align-middle" />
                                    : `${fmtCount(counts[v])} ${counts[v] === 1 ? "usuário" : "usuários"}`}
                            </p>
                        )}
                    </button>
                );
            })}
        </div>
    );
}

const MAX_BOTS = 5;

/* ── Bot picker ── */
function BotPicker({ bots, photoUrls = {}, value, onChange, principalBotId = null }) {
    const toggle = (id) => {
        if (value.includes(id)) {
            onChange(value.filter((x) => x !== id));
        } else {
            const bot = bots.find((b) => b.id === id);
            if (bot?.is_active === false) return; // não permite selecionar bot inativo
            if (value.length >= MAX_BOTS) return; // silently block; label already shows limit
            onChange([...value, id]);
        }
    };

    if (!bots.length) return (
        <p className="text-xs text-white/40">Nenhum bot disponível.</p>
    );

    const atLimit = value.length >= MAX_BOTS;

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between">
                <span className={`text-xs ${atLimit ? "text-amber-400" : "text-white/30"}`}>
                    {value.length}/{MAX_BOTS} bots selecionados
                    {atLimit && " — limite atingido"}
                </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {bots.map((bot) => {
                    const active = value.includes(bot.id);
                    const isPrincipal = principalBotId && bot.id === principalBotId;
                    const photo = photoUrls[bot.id];
                    const isInactive = bot.is_active === false;
                    // Bot inativo não pode ser selecionado (a menos que já esteja no disparo).
                    const disabled = !active && (atLimit || isInactive);
                    return (
                        <button
                            key={bot.id}
                            type="button"
                            onClick={() => toggle(bot.id)}
                            disabled={disabled}
                            title={isPrincipal ? "Bot principal — não pode ser removido" : isInactive ? "Bot inativo — não pode ser selecionado" : undefined}
                            className={[
                                "p-3 rounded-xl border-2 text-left transition-all",
                                active
                                    ? "border-[var(--purple)] bg-[var(--purple)]/5 cursor-pointer"
                                    : disabled
                                        ? "border-white/[0.04] bg-white/[0.01] opacity-40 cursor-not-allowed"
                                        : "border-white/[0.06] bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04] cursor-pointer",
                            ].join(" ")}
                        >
                            <div className="flex items-center gap-2.5 min-w-0">
                                <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 flex items-center justify-center bg-white/[0.06] border border-white/[0.08]">
                                    {photo
                                        ? <img src={photo} alt="" className="w-full h-full object-cover" />
                                        : <Bot size={13} className={active ? "text-[var(--purple)]" : "text-white/40"} />
                                    }
                                </div>
                                <div className="min-w-0">
                                    <span className={`text-sm font-medium truncate block ${isInactive ? "text-red-500" : active ? "text-[var(--purple)]" : "text-white/80"}`}>
                                        {bot.bot_username || bot.bot_name}
                                        {isInactive && <span className="text-red-500"> - inativo</span>}
                                    </span>
                                    <span className="text-[10px] leading-none text-white/50">
                                        <span className="inline-flex items-center gap-1"><MarketIcon mode={normalizeMode(bot.currency_mode)} size={10} />{baseCurrencyOf(bot.currency_mode, bot.base_currency)}</span>
                                    </span>
                                    {isPrincipal && (
                                        <span className="text-[10px] text-amber-400/70 leading-none ml-1">principal</span>
                                    )}
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/* ── Step bar ── */
function StepBar({ current }) {
    return (
        <div className="flex items-center gap-0 mb-8">
            {STEPS.map((step, idx) => {
                const done = current > step.id;
                const active = current === step.id;
                const Icon = step.icon;
                return (
                    <div key={step.id} className="flex items-center flex-1 last:flex-none">
                        <div className="flex flex-col items-center gap-1.5 shrink-0">
                            <div className={[
                                "flex items-center justify-center w-9 h-9 rounded-full border-2 transition-all",
                                done ? "border-[var(--purple)] bg-[var(--purple)] text-white"
                                    : active ? "border-[var(--purple)] bg-[var(--purple)]/10 text-[var(--purple)]"
                                        : "border-white/[0.12] bg-white/[0.02] text-white/30",
                            ].join(" ")}>
                                {done ? <Check size={15} /> : <Icon size={15} />}
                            </div>
                            <span className={`text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${active ? "text-white" : done ? "text-[var(--purple-light)]" : "text-white/30"
                                }`}>
                                {step.label}
                            </span>
                        </div>
                        {idx < STEPS.length - 1 && (
                            <div
                                className="h-px flex-1 mx-2 mt-[-18px] transition-all"
                                style={{ background: done ? "var(--purple)" : "rgba(255,255,255,0.08)" }}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/* ─────────────────────────────────────────────────────────────────────────── */

export default function DisparoForm({ bots = [], editing, onEditingChange, onSuccess, onBack }) {
    const { showError, showSuccess } = useNotification();
    const mediaRef = useRef();
    const bumpRef = useRef();
    const photoUrls = useBotPhotos(bots);

    const [form, setForm] = useState({
        id: null,
        name: "",
        message: "",
        pix_values: [],
        plan_names: [],
        plan_duration: [],
        plan_color: [],
        plan_deliveries: [],
        target_audience: "Todos Usuários",
        bot_ids: [],
    });

    const [initialPlans, setInitialPlans] = useState(null);
    const [existingMedia, setExistingMedia] = useState({ photo: null, video: null, audio: null });
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);

    // Quantos usuários cada público alcança nos bots escolhidos. Só busca com o
    // seletor de público visível (passo 2 ou edição) e espera o usuário parar de
    // mexer nos bots; o backend usa COUNT + cache de 5 min.
    const [audienceCounts, setAudienceCounts] = useState(null);
    const [audienceCountsLoading, setAudienceCountsLoading] = useState(false);
    const audienceVisible = !!editing?.id || step === 2;
    const botIdsKey = [...form.bot_ids].sort((a, b) => a - b).join(",");
    useEffect(() => {
        if (!audienceVisible || !botIdsKey) { setAudienceCounts(null); return; }
        let cancelled = false;
        setAudienceCountsLoading(true);
        const t = setTimeout(() => {
            authService.disparo.audienceCounts(botIdsKey.split(","))
                .then(({ data }) => { if (!cancelled) setAudienceCounts(data?.counts || null); })
                .catch(() => { if (!cancelled) setAudienceCounts(null); })
                .finally(() => { if (!cancelled) setAudienceCountsLoading(false); });
        }, 400);
        return () => { cancelled = true; clearTimeout(t); };
    }, [audienceVisible, botIdsKey]);

    // Moeda dos valores dos bots selecionados. Um disparo não pode misturar moedas
    // (os valores dos planos seriam ambíguos): nem modos diferentes, nem bots LATAM
    // com moedas-base diferentes (MXN x USD...).
    const selectedBots = bots.filter((b) => form.bot_ids.includes(b.id));
    const selectedCurrencies = [...new Set(selectedBots.map((b) => normalizeMode(b.currency_mode)))];
    const selectedBases = [...new Set(selectedBots.map((b) => baseCurrencyOf(b.currency_mode, b.base_currency)))];
    const mixedCurrency = selectedBases.length > 1;
    const disparoCurrency = selectedCurrencies[0] || 'brl';
    const disparoBase = selectedBases[0] || null;
    const currencyModeForPlans = disparoCurrency; // 'brl' | 'usd' | 'mxn'
    // "Apenas Upsell" indisponível se algum bot do disparo é Global ou tem cartão.
    const gateways = useGatewayList();
    const authUser = useAuth()?.user;
    const onlyUpsellBlocked = isOnlyUpsellBlocked({
        currencyMode: selectedCurrencies.includes('usd') ? 'usd' : 'brl',
        gateways: gateways || [],
        botIds: form.bot_ids,
        isAdmin: authUser?.is_admin === true,
    });

    useEffect(() => {
        if (editing) {
            setForm({
                id: editing.id,
                name: editing.name,
                message: editing.message,
                pix_values: editing.pix_values || [],
                plan_names: editing.plan_names || [],
                plan_duration: editing.plan_duration || [],
                plan_color: editing.plan_color || editing.plan_colors || [],
                plan_deliveries: deliveriesFromApi(editing),
                target_audience: editing.target_audience || "Todos Usuários",
                bot_ids: editing.bot_ids || [],
                action: "resend",
                old_offer_id: editing.id || null,
            });
            setInitialPlans({
                names: editing.plan_names || [],
                values: editing.pix_values || [],
                durations: editing.plan_duration || [],
                color: editing.plan_color || editing.plan_colors || [],
                deliveries: deliveriesFromApi(editing),
            });
            setExistingMedia({
                photo: editing.image_file_id || null,
                video: editing.video_file_id || null,
                audio: editing.audio_file_id || null,
            });
        } else {
            resetForm();
        }
    }, [editing]);

    const handlePlansUpdate = (updatedPlans) => {
        setForm((prev) => ({
            ...prev,
            plan_names: updatedPlans.names,
            pix_values: updatedPlans.values,
            plan_color: updatedPlans.color,
            plan_duration: updatedPlans.durations,
            plan_deliveries: updatedPlans.deliveries || [],
        }));
    };

    const resetForm = () => {
        setForm({
            id: null, name: "", message: "",
            pix_values: [], plan_names: [], plan_duration: [], plan_color: [],
            plan_deliveries: [],
            target_audience: "Todos Usuários", bot_ids: [],
        });
        setExistingMedia({ photo: null, video: null, audio: null });
        setInitialPlans(null);
        setStep(1);
        onEditingChange(null);
    };

    const handleSubmit = async (e) => {
        e?.preventDefault();

        if (!form.bot_ids.length) {
            showError("Selecione ao menos um bot para este disparo.");
            return;
        }
        if (form.bot_ids.length > MAX_BOTS) {
            showError(`Máximo de ${MAX_BOTS} bots por disparo.`);
            return;
        }
        if (mixedCurrency) {
            showError("Este disparo mistura bots de moedas diferentes. Deixe todos na mesma moeda — troque o bot, ou ajuste a moeda-base do bot.");
            return;
        }
        const planError = planValueError(form.pix_values, disparoCurrency, disparoBase);
        if (planError) {
            showError(planError);
            return;
        }
        const contentCheck = validateFields([
            { label: "Nome da Oferta", value: form.name },
            { label: "Mensagem do Disparo", value: form.message },
            ...(form.plan_names || []).map((name, i) => ({ label: `Plano ${i + 1} - Nome`, value: name })),
        ]);
        if (!contentCheck.isValid) { showError(contentCheck.error); return; }

        // Valida o Order Bump antes de criar o disparo (se inválido, nem cria).
        const bumpErr = bumpRef.current?.validate?.();
        if (bumpErr) { showError(bumpErr); return; }

        setLoading(true);
        try {
            const payload = {
                ...form,
                plan_colors: form.plan_color,
                ...(editing ? {
                    kept_image: existingMedia.photo || null,
                    kept_video: existingMedia.video || null,
                    kept_audio: existingMedia.audio || null,
                } : {}),
            };
            const { data } = await authService.disparo.save(payload);
            if (!data.success) throw new Error(data.error);

            const disparoId = data.offer_id || form.id;
            // A mídia é enviada para TODOS os bots do disparo de forma síncrona.
            // Se não conseguir entregar a todos, o backend retorna erro — aqui o
            // disparo NÃO é finalizado (sem sucesso, sem redirect) para o usuário
            // corrigir o bot e tentar de novo. O toast de erro já é exibido no upload.
            let mediaResult = { success: true };
            try {
                if (mediaRef.current && disparoId) {
                    mediaResult = await mediaRef.current.uploadAll(disparoId);
                }
            } catch {
                showError("Falha no upload da mídia. Tente novamente.");
                return;
            }
            if (mediaResult && mediaResult.success === false) {
                // Erro específico já mostrado pelo upload; mantém o usuário no form.
                return;
            }

            // Order bump personalizado — salvo JUNTO, já com o disparo criado.
            try {
                await bumpRef.current?.save?.(disparoId);
            } catch (e) {
                showError(e?.response?.data?.message || e.message || "Erro ao salvar Order Bump");
                return;
            }

            showSuccess("Disparo salvo com sucesso!");
            resetForm();
            onSuccess();
        } catch (err) {
            showError(err?.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    const canAdvance = () => {
        if (step === 1) return form.bot_ids.length > 0 && form.name.trim().length > 0 && !mixedCurrency;
        if (step === 2) return !!form.target_audience;
        if (step === 3) return form.message.trim().length > 0;
        return true;
    };

    const advance = () => {
        if (!canAdvance()) {
            if (step === 1) {
                if (!form.bot_ids.length) showError("Selecione ao menos um bot.");
                else if (mixedCurrency) showError("Este disparo mistura bots de moedas diferentes (BRL, USD ou MXN). Deixe todos na mesma moeda antes de continuar.");
                else showError("Preencha o nome da oferta.");
            }
            if (step === 2) showError("Selecione o público-alvo.");
            if (step === 3) showError("Escreva a mensagem do disparo.");
            return;
        }
        setStep((s) => s + 1);
    };

    const isEditing = !!editing?.id;

    /* ══════════════════════════════════════════════════════════════════════
       EDIT MODE — card sections
    ══════════════════════════════════════════════════════════════════════ */
    if (isEditing) {
        return (
            <form onSubmit={handleSubmit} style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? "none" : "auto" }}>
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <button
                        type="button"
                        onClick={onBack}
                        className="p-2 rounded-lg bg-white/[0.02] border border-white/[0.06] text-white/50 hover:text-white hover:bg-white/[0.04] transition-colors cursor-pointer"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-white">Editar Disparo</h1>
                        <p className="text-sm text-white/40 mt-0.5">Modifique as configurações do disparo</p>
                    </div>
                </div>

                <div className="space-y-6">
                    {/* Informações Básicas */}
                    <div>
                        <h2 className="text-base font-semibold text-white mb-5 flex items-center gap-2">
                            <Sparkles size={17} className="text-[var(--purple-light)]" />
                            Informações Básicas
                        </h2>
                        <div className="space-y-5">
                            {/* Bots */}
                            <div className="space-y-2.5">
                                <label className="spt-label flex items-center gap-2">
                                    Bots <span className="text-red-400">*</span>
                                    {form.bot_ids.length > 0 && !mixedCurrency && (
                                        <span className="text-[11px] font-normal text-white/50">
                                            — moeda do disparo: <span className="inline-flex items-center gap-1 align-middle"><MarketIcon mode={disparoCurrency} size={11} />{baseCurrencyOf(disparoCurrency, disparoBase)}</span>
                                        </span>
                                    )}
                                </label>
                                <BotPicker
                                    bots={bots}
                                    photoUrls={photoUrls}
                                    value={form.bot_ids}
                                    principalBotId={editing?.bot_id ?? form.bot_ids[0] ?? null}
                                    onChange={(ids) => {
                                        const principal = editing?.bot_id ?? form.bot_ids[0] ?? null;
                                        if (principal && !ids.includes(principal)) return; // guard
                                        setForm({ ...form, bot_ids: ids });
                                    }}
                                />
                                {mixedCurrency && (
                                    <div className="mt-2 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                                        <i className="fas fa-exclamation-triangle mt-0.5" />
                                        <span>
                                            Você misturou bots de moedas diferentes (<strong>BRL</strong>, <strong>USD</strong> ou <strong>MXN</strong>). Um disparo só pode ter
                                            uma moeda. Remova os bots de moeda diferente, ou ajuste a moeda-base do bot na aba Pagamentos.
                                        </span>
                                    </div>
                                )}
                            </div>
                            {/* Nome */}
                            <div className="space-y-1.5">
                                <label className="spt-label">
                                    Nome da Oferta <span className="text-red-400">*</span>
                                </label>
                                <input
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    required
                                    placeholder="Ex: Disparo — Oferta de Recuperação"
                                    className="input3"
                                />
                                <p className="form-hint">Nome interno. Não exibido para usuários.</p>
                            </div>
                            {/* Público */}
                            <div className="space-y-2.5">
                                <label className="spt-label">
                                    Público-alvo <span className="text-red-400">*</span>
                                </label>
                                <AudienceGrid
                                    value={form.target_audience}
                                    onChange={(v) => setForm({ ...form, target_audience: v })}
                                    counts={audienceCounts}
                                    countsLoading={audienceCountsLoading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Mensagem */}
                    <div>
                        <h2 className="text-base font-semibold text-white mb-5 flex items-center gap-2">
                            <MessageSquare size={17} className="text-[var(--purple-light)]" />
                            Conteúdo da Mensagem
                        </h2>
                        <div className="space-y-4">
                            <div className="space-y-1.5">
                                <label className="spt-label">
                                    Mensagem <span className="text-red-400">*</span>
                                </label>
                                <TextForm
                                    value={form.message}
                                    onChange={(value) => setForm({ ...form, message: value })}
                                    hint="Mensagem enviada ao cliente com a oferta do disparo."
                                />
                            </div>
                            <MediaManager
                                ref={mediaRef}
                                botId={form.bot_ids[0] || null}
                                targetType="offer"
                                targetId={null}
                                existingMedia={existingMedia}
                                onMediaChange={setExistingMedia}
                                uploadContext="disparo"
                            />
                        </div>
                    </div>

                    {/* Planos */}
                    <div>
                        <h2 className="text-base font-semibold text-white mb-5 flex items-center gap-2">
                            <Package size={17} className="text-[var(--purple-light)]" />
                            Planos de Pagamento
                        </h2>
                        <PlanManager initialPlans={initialPlans} onUpdate={handlePlansUpdate} label="" currencyMode={currencyModeForPlans} baseCurrency={disparoBase} enableDelivery={true} onlyUpsellBlocked={onlyUpsellBlocked} />
                    </div>

                    {/* Order bump personalizado — salvo junto com o disparo.
                        Multi-bot: o bump vale p/ todos os bots; a mídia usa o 1º bot. */}
                    <div className="mt-8">
                        <PersonalizedOrderBump
                            ref={bumpRef}
                            botId={form.bot_ids?.[0] || null}
                            offerId={form.id}
                            uploadContext="disparo_orderbump"
                        />
                    </div>
                </div>

                <div className="flex items-center justify-end gap-3 mt-6">
                    <button type="button" onClick={onBack} className="btn-secondary">
                        <ArrowLeft size={14} /> Voltar
                    </button>
                    <button type="submit" disabled={loading} className="btn-primary">
                        <Save size={14} />
                        {loading ? "Salvando..." : "Salvar Alterações"}
                    </button>
                </div>
            </form>
        );
    }

    /* ══════════════════════════════════════════════════════════════════════
       CREATE MODE — 3-step wizard
    ══════════════════════════════════════════════════════════════════════ */
    return (
        <div style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? "none" : "auto" }}>
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    type="button"
                    onClick={step === 1 ? onBack : () => setStep((s) => s - 1)}
                    className="p-2 rounded-lg bg-white/[0.02] border border-white/[0.06] text-white/50 hover:text-white hover:bg-white/[0.04] transition-colors cursor-pointer"
                >
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-white">Novo Disparo</h1>
                    <p className="text-sm text-white/40 mt-0.5">Configure um disparo para recuperar vendas</p>
                </div>
            </div>

            <StepBar current={step} />

            {/* Step content */}
            <div>
                {/* Step 1 — Configuração */}
                {step === 1 && (
                    <div className="space-y-5">
                        <h2 className="text-base font-semibold text-white flex items-center gap-2">
                            <Sparkles size={17} className="text-[var(--purple-light)]" />
                            Configuração
                        </h2>

                        {/* Nome */}
                        <div className="space-y-1.5">
                            <label className="spt-label">
                                Nome da Oferta <span className="text-red-400">*</span>
                            </label>
                            <input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                autoFocus
                                placeholder="Ex: Disparo — Oferta de Recuperação"
                                className="input3"
                            />
                            <p className="form-hint">Nome interno. Não exibido para usuários.</p>
                        </div>

                        {/* Bots */}
                        <div className="space-y-2.5">
                            <label className="spt-label">
                                Bots <span className="text-red-400">*</span>
                            </label>
                            <BotPicker
                                bots={bots}
                                photoUrls={photoUrls}
                                value={form.bot_ids}
                                onChange={(ids) => setForm({ ...form, bot_ids: ids })}
                            />
                            <p className="form-hint">O disparo será enviado por todos os bots selecionados.</p>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="space-y-2.5">
                        <label className="spt-label">
                            Público-alvo <span className="text-red-400">*</span>
                        </label>
                        <AudienceGrid
                            value={form.target_audience}
                            onChange={(v) => setForm({ ...form, target_audience: v })}
                            counts={audienceCounts}
                            countsLoading={audienceCountsLoading}
                        />
                    </div>
                )}

                {/* Step 3 — Mensagem (always mounted to preserve mediaRef across steps) */}
                <div className={step === 3 ? "space-y-4" : "hidden"}>
                    <h2 className="text-base font-semibold text-white flex items-center gap-2">
                        <MessageSquare size={17} className="text-[var(--purple-light)]" />
                        Conteúdo da Mensagem
                    </h2>
                    <div className="space-y-1.5">
                        <label className="spt-label">
                            Mensagem <span className="text-red-400">*</span>
                        </label>
                        <TextForm
                            value={form.message}
                            onChange={(value) => setForm({ ...form, message: value })}
                            hint="Mensagem enviada ao cliente com a oferta do disparo."
                        />
                    </div>
                    <MediaManager
                        ref={mediaRef}
                        botId={form.bot_ids[0] || null}
                        targetType="offer"
                        targetId={null}
                        existingMedia={existingMedia}
                        onMediaChange={setExistingMedia}
                        uploadContext="disparo"
                    />
                </div>

                {/* Step 3 — Planos */}
                {step === 4 && (
                    <div className="space-y-4">
                        <h2 className="text-base font-semibold text-white flex items-center gap-2">
                            <Package size={17} className="text-[var(--purple-light)]" />
                            Planos de Pagamento
                        </h2>
                        <PlanManager initialPlans={null} onUpdate={handlePlansUpdate} label="" currencyMode={currencyModeForPlans} baseCurrency={disparoBase} enableDelivery={true} onlyUpsellBlocked={onlyUpsellBlocked} />

                        {/* Order bump personalizado — configurado desde a criação;
                            salvo junto ao clicar em "Criar Disparo". */}
                        <PersonalizedOrderBump
                            ref={bumpRef}
                            botId={form.bot_ids?.[0] || null}
                            offerId={form.id}
                            uploadContext="disparo_orderbump"
                        />
                    </div>
                )}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-6">
                <button
                    type="button"
                    onClick={step === 1 ? onBack : () => setStep((s) => s - 1)}
                    className="btn-secondary btn-action"
                >
                    <ArrowLeft size={14} />
                    {step === 1 ? "Cancelar" : "Anterior"}
                </button>

                {step < 4 ? (
                    <button type="button" onClick={advance} className="btn-primary btn-action">
                        Próximo <ArrowRight size={14} />
                    </button>
                ) : (
                    <button type="button" onClick={handleSubmit} disabled={loading} className="btn-primary btn-action">
                        <Check size={14} />
                        {loading ? "Criando..." : "Criar Disparo"}
                    </button>
                )}
            </div>
        </div >
    );
}
